import React from "react";
import { Virtuoso } from "react-virtuoso";
import { Box, makeStyles } from "@material-ui/core";
import clsx from "clsx";

import type { CoretexTheme } from "../theme";
import SectionExpand from "../tracking/SectionExpand";
import type { ListRecordResponse } from "./DynamicInfiniteList";
import DynamicInfiniteList, { listStyles } from "./DynamicInfiniteList";

const useStyles = makeStyles((theme: CoretexTheme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
  },
  favourites: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    maxHeight: "20%",
  },
  groups: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  divider: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.mono.white,
    height: 28,
    width: "100%",
  },
  line: {
    width: "60px",
    height: "4px",
    backgroundColor: theme.palette.mono.gray,
    borderRadius: theme.shape.borderRadius,
  },
  ...listStyles,
}));

export interface InfiniteGroup {
  id: string | number;
  title: string;
  records?: number;
  loadItems?: (page: number) => Promise<ListRecordResponse<unknown>>;
  data?: unknown[];
  className?: string;
  isExpanded?: boolean;
}

interface GroupedInfiniteListProps {
  renderItem(index: number, data: unknown): React.ReactNode;
  groups: InfiniteGroup[];
  favourites?: unknown[];
  LoadingPlaceholder?: React.ReactNode;
}

const Divider = (): JSX.Element => {
  const classes = useStyles();
  return (
    <Box className={classes.divider}>
      <Box className={classes.line} />
    </Box>
  );
};

const GroupedInfiniteList = ({
  renderItem,
  favourites,
  groups,
  LoadingPlaceholder,
}: GroupedInfiniteListProps): JSX.Element => {
  const classes = useStyles();

  const [expandedGroup, setExpandedGroup] = React.useState<Record<string | number, boolean>>(
    groups.reduce((obj: Record<string | number, boolean>, group) => {
      obj[group.id] = false;
      return obj;
    }, {}),
  );

  const renderFavourites = () => {
    if (!favourites) return <></>;

    const loadFavourites = async (page: number): Promise<ListRecordResponse<unknown>> => {
      if (page === 1) {
        return {
          items: favourites,
          records: favourites.length,
        };
      }

      return {
        items: [],
        records: 0,
      };
    };

    return (
      <Box className={classes.favourites}>
        <DynamicInfiniteList renderItem={renderItem} loadItems={loadFavourites} />
        <Divider />
      </Box>
    );
  };

  const toggleGroup = (id: number | string) => () => {
    setExpandedGroup({ ...expandedGroup, [id]: !expandedGroup[id] });
  };

  return (
    <Box className={classes.root}>
      {renderFavourites()}
      <Box className={classes.groups}>
        {groups &&
          groups.map(({ id, title, records, loadItems, className, data, isExpanded }: InfiniteGroup) => {
            const isGroupExpanded = isExpanded || (expandedGroup && expandedGroup[id]);

            const dummyLoad = (): Promise<ListRecordResponse<unknown>> =>
              new Promise((resolve) =>
                resolve({
                  items: [],
                  records: 0,
                }),
              );

            return (
              <SectionExpand key={id} title={title} onToggle={toggleGroup(id)} expanded={isGroupExpanded}>
                {data ? (
                  <Virtuoso
                    className={clsx(className, classes.list)}
                    totalCount={records}
                    data={data}
                    itemContent={renderItem}
                  />
                ) : (
                  <DynamicInfiniteList
                    renderItem={renderItem}
                    loadItems={loadItems || dummyLoad}
                    className={className}
                    LoadingPlaceholder={LoadingPlaceholder}
                  />
                )}
              </SectionExpand>
            );
          })}
      </Box>
    </Box>
  );
};

export default GroupedInfiniteList;
